import React, { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

const CustomCursor = () => {
  /* Raw cursor position — instant */
  const cursorX = useMotionValue(-200);
  const cursorY = useMotionValue(-200);

  /* Inner ring — faster spring, tighter lag */
  const innerX = useSpring(cursorX, { stiffness: 180, damping: 22, mass: 0.5 });
  const innerY = useSpring(cursorY, { stiffness: 180, damping: 22, mass: 0.5 });

  /* Outer ring — slower spring, more lag */
  const outerX = useSpring(cursorX, { stiffness: 80, damping: 18, mass: 0.8 });
  const outerY = useSpring(cursorY, { stiffness: 80, damping: 18, mass: 0.8 });

  const [isVisible, setIsVisible] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () =>
      setIsMobile(window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);

    const onMove = (e) => {
      cursorX.set(e.clientX);
      cursorY.set(e.clientY);
      setIsVisible(true);
    };
    const onEnter = () => setIsVisible(true);
    const onLeave = () => setIsVisible(false);
    const onDown = () => setIsClicking(true);
    const onUp = () => setIsClicking(false);

    const onOver = (e) => {
      const el = e.target;
      const interactive =
        el.closest("a") ||
        el.closest("button") ||
        el.closest("[role='button']") ||
        el.closest("input") ||
        el.closest("textarea") ||
        el.closest("select") ||
        window.getComputedStyle(el).cursor === "pointer";
      setIsHovering(!!interactive);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseenter", onEnter);
    window.addEventListener("mouseleave", onLeave);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    document.addEventListener("mouseover", onOver);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseenter", onEnter);
      window.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      document.removeEventListener("mouseover", onOver);
      window.removeEventListener("resize", checkMobile);
    };
  }, [cursorX, cursorY]);

  if (isMobile) return null;

  const outerSize = isHovering ? 56 : 40;
  const innerSize = isHovering ? 24 : 18;

  return (
    <>
      {/* ── Outer target ring — slow spring ── */}
      <motion.div
        className="fixed top-0 left-0 pointer-events-none z-[9999] rounded-full"
        style={{
          x: outerX,
          y: outerY,
          translateX: "-50%",
          translateY: "-50%",
          width: outerSize,
          height: outerSize,
          border: `1.5px solid rgba(255,215,0,${isHovering ? 0.9 : 0.55})`,
          boxShadow: isHovering
            ? "0 0 12px rgba(255,215,0,0.35), inset 0 0 8px rgba(255,215,0,0.08)"
            : "0 0 6px rgba(255,215,0,0.18)",
        }}
        animate={{
          opacity: isVisible ? 1 : 0,
          scale: isClicking ? 0.8 : 1,
          width: outerSize,
          height: outerSize,
        }}
        transition={{ duration: 0.25, ease: "easeOut" }}
      />

      {/* ── Inner target ring — faster spring ── */}
      <motion.div
        className="fixed top-0 left-0 pointer-events-none z-[9999] rounded-full"
        style={{
          x: innerX,
          y: innerY,
          translateX: "-50%",
          translateY: "-50%",
          width: innerSize,
          height: innerSize,
          border: `1px solid rgba(255,215,0,${isHovering ? 0.7 : 0.4})`,
        }}
        animate={{
          opacity: isVisible ? 1 : 0,
          scale: isClicking ? 0.6 : 1,
          width: innerSize,
          height: innerSize,
        }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      />

      {/* ── Centre dot — instant, no lag ── */}
      <motion.div
        className="fixed top-0 left-0 pointer-events-none z-[9999] rounded-full bg-primary"
        style={{
          x: cursorX,
          y: cursorY,
          translateX: "-50%",
          translateY: "-50%",
          width: 3,
          height: 3,
          boxShadow: "0 0 6px rgba(255,215,0,0.9)",
        }}
        animate={{ opacity: isVisible ? (isHovering ? 0 : 0.85) : 0 }}
        transition={{ duration: 0.15 }}
      />
    </>
  );
};

export default CustomCursor;
